import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaEye,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFilter,
  FaUser,
  FaGlobe,
  FaVideo,
  FaClock,
  FaCamera,
  FaDownload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendar,
  FaMoneyBillWave,
  FaTag,
  FaPlay,
  FaImage,
  FaFileAlt
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";

const ReporterCompletedAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectAdInfo, setRejectAdInfo] = useState(null); // { adId, reporterId }
  const [showApproveSpinner, setShowApproveSpinner] = useState(false); // Spinner state
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);   // Spinner for reject
  // Search state
  const [search, setSearch] = useState("");
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  // imagwe screenshot URL function
  const getScreenshotUrl = (path) => {
    if (!path) return "";
    // Replace backslashes with forward slashes for URLs
    const cleanPath = path.replace(/\\/g, "/");
    return `${process.env.REACT_APP_API_BASE_URL}/${cleanPath}`;
  };

  useEffect(() => {
    const fetchCompletedAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/completed/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Fetched Ads Data:", response);

        setAds(response.data.data || []);
      } catch (err) {
        console.error("Error fetching ads:", err.response || err);
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedAds();
  }, []);

  console.log("Ads Data:", ads);

  // Approve handler
  const handleApprove = async (adId, reporterId) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/approve/completed/ad`,
        { adId, reporterId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.success) {
        toast.success("Ad approved successfully!");
        setTimeout(() => {
          window.location.reload(true);
        }, 1200);
      } else {
        toast.error("Failed to approve ad.");
      }
    } catch (err) {
      toast.error("Failed to approve ad.");
    } finally {
      setShowApproveSpinner(false);
    }
  };

  // handle reject with reason
  const handleReject = async (adId, reporterId, reason) => {
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/completed/ad`,
        { adId, reporterId, rejectNote: reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Ad rejected successfully", adId, reporterId, reason);
      console.log("reject Response Data:", response.data);
      if (response.data.message) {
        toast.success("Ad rejected successfully!");
        setTimeout(() => {
          window.location.reload(true);
        }, 1200);
      }
    } catch (err) {
      toast.error("Failed to reject ad.");
    } finally {
      setShowRejectSpinner(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading completed ads...</div>;
  }

  // Filtered ads based on search
  const filteredAds = ads.filter(ad => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    // Check adId, iinsafId, channelName, or reporter name in proofs
    if (ad.adId && ad.adId.toLowerCase().includes(q)) return true;
    if (ad.proofs && ad.proofs.some(proof =>
      (proof.iinsafId && proof.iinsafId.toLowerCase().includes(q)) ||
      (proof.channelName && proof.channelName.toLowerCase().includes(q)) ||
      (proof.reporterName && proof.reporterName.toLowerCase().includes(q))
    )) return true;
    return false;
  });

  // Pagination logic
  const totalRows = filteredAds.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedAds = [...filteredAds].reverse().slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 mb-6 md:mb-8 text-white relative overflow-hidden mx-2 md:mx-0 mt-[-10px] md:mt-0">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                <FaCheckCircle className="w-5 h-5 md:w-8 md:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-lg md:text-3xl font-black mb-0 md:mb-1 uppercase tracking-tight">Completed Ads</h1>
                <p className="text-emerald-50 text-[10px] md:text-sm font-medium opacity-80 hidden md:block">Audit and finalize reporter submissions</p>
                <p className="text-emerald-50 text-[10px] md:text-sm font-black md:hidden uppercase tracking-widest">Auditing Tool</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl md:text-4xl font-black leading-tight">{ads.length}</div>
              <div className="text-emerald-50 text-[10px] md:text-xs font-black uppercase tracking-widest opacity-80">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl shadow-gray-200/40 border border-white p-3 md:p-6 mb-8 mx-2 md:mx-0">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-emerald-500 text-gray-400">
              <FaSearch className="w-4 h-4" />
            </div>
            <input
              type="text"
              className="w-full pl-11 pr-4 py-3.5 md:py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 focus:bg-white transition-all outline-none text-sm md:text-base font-bold shadow-inner"
              placeholder="Search campaigns, reporters or keys..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="h-[54px] md:h-auto px-10 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-[0.15em] text-xs hover:bg-black transition-all flex items-center justify-center gap-3 shadow-lg active:scale-95">
            <FaFilter className="w-3 h-3" />
            <span className="md:inline">Custom Filter</span>
          </button>
        </div>
      </div>
      {/* Spinner Overlay for Approve */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Approval...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {/* Spinner Overlay for Reject */}
      {showRejectSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-red-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#e53e3e] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#e53e3e] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#e53e3e] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-red-700 tracking-wide">
              Processing Rejection...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {/* Main Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="relative">
            <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 md:w-16 md:h-16 border-4 border-transparent border-t-emerald-400 rounded-full animate-spin" style={{ animationDuration: '1.5s' }}></div>
          </div>
        </div>
      ) : ads.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-16 text-center mx-2 md:mx-0">
          <div className="w-16 h-16 md:w-24 md:h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="w-8 h-8 md:w-12 md:h-12 text-gray-300" />
          </div>
          <h3 className="text-xl md:text-2xl font-black text-gray-800 mb-3 uppercase tracking-tight">
            No Completed Ads
          </h3>
          <p className="text-gray-500 text-sm md:text-lg font-medium max-w-md mx-auto">
            Everything is up to date. No reporter submissions are pending audit right now.
          </p>
        </div>
      ) : (
        <div className="mx-2 md:mx-0 space-y-6 md:space-y-8 pb-20">
          {paginatedAds.map((ad) => (
            <div key={ad.adId} className="bg-white rounded-2xl md:rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden group">
              {/* Ad Header Dashboard Style */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 md:px-8 py-4 md:py-6 text-white">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-emerald-500 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <FaTag className="w-5 h-5 md:w-7 md:h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base md:text-xl font-black uppercase tracking-tight flex items-center gap-2 flex-wrap">
                        Campaign: {ad.adId}
                        {ad.proofs?.length >= ad.requiredReporter && (
                          <span className="bg-emerald-500 text-[8px] md:text-[10px] px-2 py-0.5 rounded-full font-black text-white ml-2 animate-pulse">FULL</span>
                        )}
                        {ad.finalReporterPrice > 0 ? (
                          <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-[8px] md:text-[10px] px-2 py-1 rounded-full font-black text-gray-900 shadow-lg">
                            💰 PAID
                          </span>
                        ) : (
                          <span className="bg-gradient-to-r from-blue-400 to-cyan-500 text-[8px] md:text-[10px] px-2 py-1 rounded-full font-black text-white shadow-lg">
                            🎁 FREE
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 underline decoration-emerald-500/50 underline-offset-4 decoration-2">
                        <p className="text-[10px] md:text-sm text-gray-400 font-bold uppercase tracking-widest">
                          {ad.proofs?.length} OF {ad.requiredReporter} REPORTERS
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between md:flex-col md:items-end bg-gray-800/50 md:bg-transparent p-3 md:p-0 rounded-xl border border-white/5 md:border-0">
                    <div className="text-[9px] md:text-xs text-emerald-400 font-black uppercase tracking-widest mb-0 md:mb-1">Budget Per Unit</div>
                    <div className="text-lg md:text-3xl font-black text-white">
                      ₹{ad.finalReporterPrice?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Responsive Submissions Section */}
              <div className="p-0 md:p-2">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full border-separate border-spacing-y-2 px-4">
                    <thead>
                      <tr className="text-[#94a3b8] text-[10px] font-black uppercase tracking-[0.2em]">
                        <th className="px-6 py-4 text-left">Reporter</th>
                        <th className="px-6 py-4 text-left">Media Source</th>
                        <th className="px-6 py-4 text-left">Engagement</th>
                        <th className="px-6 py-4 text-left">Submission</th>
                        <th className="px-6 py-4 text-center">Final Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ad.proofs || []).map((proof, idx) => (
                        <tr key={idx} className="bg-[#f8fafc] hover:bg-white transition-all duration-300 rounded-xl group/row">
                          <td className="px-6 py-4 first:rounded-l-2xl">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-100 group-hover/row:border-emerald-200 transition-colors">
                                <FaUser className="text-emerald-600" />
                              </div>
                              <div className="text-sm font-black text-[#1e293b] uppercase tracking-tight">{proof.iinsafId}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black bg-purple-100 text-purple-700 uppercase tracking-wider">
                                {proof.platform}
                              </span>
                              <div className="text-[11px] font-bold text-[#64748b] truncate max-w-[150px]">{proof.channelName}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {proof.videoLink ? (
                                <a href={proof.videoLink} target="_blank" rel="noopener noreferrer"
                                  className="h-10 px-4 bg-emerald-600/10 hover:bg-emerald-600 text-emerald-600 hover:text-white rounded-xl flex items-center justify-center gap-2 transition-all font-black text-[10px] uppercase tracking-widest border border-emerald-600/20">
                                  <FaPlay className="w-3 h-3" /> WATCH
                                </a>
                              ) : <span className="text-gray-400 font-bold italic">No Link</span>}
                              <div className="text-[11px] font-black text-[#1e293b]">{proof.duration || "0:00"}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-[11px] font-bold text-[#64748b]">
                              {proof.submittedAt ? new Date(proof.submittedAt).toLocaleDateString() : "N/A"}
                            </div>
                            <div className="flex items-center gap-1 mt-1 text-emerald-500 font-black text-[9px] uppercase">
                              <FaCheckCircle className="w-2.5 h-2.5" /> {proof.status || "Ready"}
                            </div>
                          </td>
                          <td className="px-6 py-4 last:rounded-r-2xl">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => setSelectedAd({ ...ad, proof })}
                                className="w-9 h-9 bg-gray-100 hover:bg-emerald-600 text-gray-600 hover:text-white rounded-lg flex items-center justify-center transition-all">
                                <FaEye size={14} />
                              </button>
                              <button onClick={() => handleApprove(ad.adId, proof.reporterId)}
                                className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest transition-all shadow-sm shadow-emerald-500/20">
                                Approve
                              </button>
                              <button onClick={() => { setRejectAdInfo({ adId: ad.adId, reporterId: proof.reporterId }); setShowRejectModal(true); setRejectReason(""); }}
                                className="w-9 h-9 bg-red-50 hover:bg-red-600 text-red-500 hover:text-white rounded-lg flex items-center justify-center transition-all">
                                <FaTimes size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card Layout */}
                <div className="md:hidden space-y-4 p-4">
                  {(ad.proofs || []).map((proof, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-2xl border border-gray-200 p-4 relative overflow-hidden">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center">
                            <FaUser className="text-emerald-600" />
                          </div>
                          <div>
                            <div className="text-sm font-black text-gray-800 uppercase tracking-tight">{proof.iinsafId}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[9px] font-black bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase">{proof.platform}</span>
                              <span className="text-[9px] font-bold text-gray-500 uppercase">{proof.duration || "0:00"}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <button onClick={() => setSelectedAd({ ...ad, proof })} className="p-2 text-emerald-600 bg-emerald-50 rounded-lg">
                            <FaEye size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-100 p-3 mb-4 space-y-2">
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="text-gray-400 font-bold uppercase">Channel</span>
                          <span className="text-gray-700 font-black truncate max-w-[150px]">{proof.channelName}</span>
                        </div>
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="text-gray-400 font-bold uppercase">Submitted</span>
                          <span className="text-gray-700 font-black">{proof.submittedAt ? new Date(proof.submittedAt).toLocaleDateString() : "N/A"}</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        {proof.videoLink && (
                          <a href={proof.videoLink} target="_blank" rel="noopener noreferrer"
                            className="w-full h-12 bg-emerald-600 text-white rounded-xl flex items-center justify-center gap-2 font-black text-xs uppercase tracking-widest shadow-lg shadow-emerald-600/20 active:scale-95 transition-transform">
                            <FaPlay className="w-3 h-3" /> Watch Submission
                          </a>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => handleApprove(ad.adId, proof.reporterId)}
                            className="h-12 bg-gray-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest active:scale-95 transition-transform">
                            Quick Approve
                          </button>
                          <button onClick={() => { setRejectAdInfo({ adId: ad.adId, reporterId: proof.reporterId }); setShowRejectModal(true); setRejectReason(""); }}
                            className="h-12 bg-red-50 text-red-600 rounded-xl font-black text-[10px] uppercase tracking-widest border border-red-100 active:scale-95 transition-transform">
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modern Desktop Pagination */}
      {!loading && totalPages > 1 && (
        <div className="fixed bottom-0 left-0 right-0 md:relative bg-white/80 backdrop-blur-lg border-t border-gray-100 p-4 md:p-8 flex justify-center z-40">
          <nav className="inline-flex items-center gap-1 md:gap-2 bg-gray-100/50 p-1.5 rounded-2xl border border-gray-200 shadow-sm">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
            >
              <FaClock className="rotate-90 text-[10px] md:text-sm" />
            </button>
            <div className="flex items-center gap-1 px-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-xl text-xs md:text-sm font-black transition-all ${currentPage === pageNum
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 rotate-3'
                      : 'text-gray-600 hover:bg-white'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-gray-500 hover:bg-white hover:shadow-md disabled:opacity-30 transition-all active:scale-90"
            >
              <FaClock className="-rotate-90 text-[10px] md:text-sm" />
            </button>
          </nav>
        </div>
      )}






      {/* Popup Modal for Overview */}
      {selectedAd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-4 md:p-6 rounded-t-2xl sticky top-0 z-10 backdrop-blur-md">
              <button
                className="absolute top-3 right-3 text-white hover:text-gray-200 text-xl font-black w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 transition-all border border-white/20"
                onClick={() => setSelectedAd()}
                title="Close"
              >
                ×
              </button>
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <FaEye className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight">Ad Details</h3>
                  <p className="text-emerald-50 text-[10px] md:text-sm font-medium opacity-80 uppercase tracking-widest">Full Audit View</p>
                </div>
              </div>
            </div>

            <div className="p-4 md:p-8">
              {/* Screenshots Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 md:p-6 border border-blue-100">
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                      <FaImage className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-sm md:text-lg font-black text-gray-800 uppercase tracking-tight">Task Proof</h4>
                  </div>
                  {selectedAd.proofs?.[0]?.screenshot ? (
                    <div className="bg-white p-2 md:p-4 rounded-lg border border-blue-200 shadow-sm">
                      <img
                        src={getScreenshotUrl(selectedAd.proofs[0].screenshot)}
                        alt="Screenshot"
                        className="w-full max-h-48 md:max-h-64 object-contain rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="bg-white p-4 md:p-6 rounded-lg border border-blue-200 text-center">
                      <FaExclamationTriangle className="w-6 h-6 md:w-8 md:h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs md:text-sm text-gray-500 font-medium">No screenshot available</p>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 md:p-6 border border-emerald-100">
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                    <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                      <FaCheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-sm md:text-lg font-black text-gray-800 uppercase tracking-tight">Final Verdict</h4>
                  </div>
                  {selectedAd.proofs?.[0]?.completedTaskScreenshot ? (
                    <div className="bg-white p-2 md:p-4 rounded-lg border border-emerald-200 shadow-sm">
                      <img
                        src={getScreenshotUrl(
                          selectedAd.proofs[0].completedTaskScreenshot
                        )}
                        alt="Screenshot"
                        className="w-full max-h-48 md:max-h-64 object-contain rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="bg-white p-6 rounded-lg border border-green-200 text-center">
                      <FaCheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No completion screenshot available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Ad Info Section */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                  <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                    <FaTag className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="text-sm md:text-xl font-black text-gray-800 uppercase tracking-tight">Core Logistics</h4>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-gray-50/50 p-3 md:p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1 md:gap-2 mb-1">
                      <FaTag className="w-3 h-3 text-gray-400" />
                      <span className="text-[9px] md:text-sm font-black text-gray-400 uppercase tracking-widest">ID</span>
                    </div>
                    <span className="text-xs md:text-lg font-black text-gray-900 truncate block">{selectedAd.adId}</span>
                  </div>
                  <div className="bg-gray-50/50 p-3 md:p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1 md:gap-2 mb-1">
                      <FaGlobe className="w-3 h-3 text-gray-400" />
                      <span className="text-[9px] md:text-sm font-black text-gray-400 uppercase tracking-widest">Type</span>
                    </div>
                    <span className="text-xs md:text-lg font-black text-gray-900">{selectedAd.adType}</span>
                  </div>
                  <div className="bg-gray-50/50 p-3 md:p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1 md:gap-2 mb-1">
                      <FaEye className="w-3 h-3 text-gray-400" />
                      <span className="text-[9px] md:text-sm font-black text-gray-400 uppercase tracking-widest">Views</span>
                    </div>
                    <span className="text-xs md:text-lg font-black text-gray-900">{selectedAd.baseView}</span>
                  </div>
                  <div className="bg-emerald-50/50 p-3 md:p-4 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-1 md:gap-2 mb-1">
                      <FaMoneyBillWave className="w-3 h-3 text-emerald-500" />
                      <span className="text-[9px] md:text-sm font-black text-emerald-600 uppercase tracking-widest">Reward</span>
                    </div>
                    <span className="text-xs md:text-lg font-black text-emerald-700">₹{selectedAd.finalReporterPrice?.toFixed(2)}</span>
                  </div>
                  <div className="bg-blue-50/50 p-3 md:p-4 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-1 md:gap-2 mb-1">
                      <FaCheckCircle className="w-3 h-3 text-blue-500" />
                      <span className="text-[9px] md:text-sm font-black text-blue-600 uppercase tracking-widest">Status</span>
                    </div>
                    <span className="text-[10px] md:text-sm font-black text-blue-700 uppercase">{selectedAd.runningAdStatus}</span>
                  </div>
                </div>
              </div>

              {/* Reporter Details Section */}
              {selectedAd.proofs && selectedAd.proofs[0] && (
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-yellow-600 rounded-lg flex items-center justify-center">
                      <FaUser className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800">Reporter Details</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaVideo className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm font-semibold text-yellow-700 uppercase">Channel Name</span>
                      </div>
                      <span className="text-lg font-bold text-yellow-800">{selectedAd.proofs[0].channelName}</span>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaGlobe className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-700 uppercase">Platform</span>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full bg-purple-100 text-purple-800">
                        {selectedAd.proofs[0].platform}
                      </span>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaUser className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700 uppercase">Reporter ID</span>
                      </div>
                      <span className="text-lg font-bold text-blue-800">{selectedAd.proofs[0].iinsafId}</span>
                    </div>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaClock className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700 uppercase">Duration</span>
                      </div>
                      <span className="text-lg font-bold text-gray-800">{selectedAd.proofs[0].duration}</span>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaCheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-700 uppercase">Status</span>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                        {selectedAd.proofs[0].status}
                      </span>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl border border-indigo-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FaCalendar className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm font-semibold text-indigo-700 uppercase">Submitted</span>
                      </div>
                      <span className="text-sm font-bold text-indigo-800">
                        {selectedAd.proofs[0].submittedAt
                          ? new Date(selectedAd.proofs[0].submittedAt).toLocaleString()
                          : "Not available"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Task Details Section */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                    <FaFileAlt className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800">Task Information</h4>
                </div>
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <FaFileAlt className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-semibold text-gray-700 uppercase">Task Description</span>
                    </div>
                    <p className="text-gray-900 text-lg">
                      {selectedAd.proofs[0].task || "No task description available"}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <FaPlay className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700 uppercase">Video Link</span>
                    </div>
                    {selectedAd.proofs[0].videoLink ? (
                      <a
                        href={selectedAd.proofs[0].videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaPlay className="w-4 h-4 mr-2" />
                        Watch Video
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      <span className="text-gray-500 italic">No video link available</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end">
                <button
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors flex items-center gap-2 font-semibold"
                  onClick={() => setSelectedAd(null)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-slideUp">
            <div className="bg-gradient-to-br from-red-600 to-red-800 text-white p-8 relative">
              <button
                className="absolute top-6 right-6 text-white/60 hover:text-white text-3xl font-light w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-all"
                onClick={() => setShowRejectModal(false)}
              >
                ×
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 shadow-xl backdrop-blur-md border border-white/10">
                <FaTimes className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-black uppercase tracking-tight">Decline Audit</h3>
              <p className="text-red-100/80 text-xs font-bold uppercase tracking-widest mt-1">Proof Verification Failed</p>
            </div>

            <div className="p-8">
              <div className="space-y-2 mb-8">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-1">
                  Rejection Rationale
                </label>
                <textarea
                  className="w-full border-2 border-gray-100 bg-gray-50/50 rounded-2xl p-5 resize-none focus:outline-none focus:ring-4 focus:ring-red-500/10 focus:border-red-500/50 transition-all font-medium text-gray-700 leading-relaxed shadow-inner"
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain clearly why this submission doesn't meet requirements..."
                />
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    if (rejectAdInfo && rejectReason.trim()) {
                      handleReject(rejectAdInfo.adId, rejectAdInfo.reporterId, rejectReason.trim());
                      setShowRejectModal(false);
                    }
                  }}
                  disabled={!rejectReason.trim()}
                  className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-lg ${rejectReason.trim()
                    ? "bg-red-600 text-white hover:bg-red-700 shadow-red-500/20 active:scale-95"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                >
                  Finalize Rejection
                </button>
                <button
                  className="w-full py-4 text-gray-500 font-bold text-[10px] uppercase tracking-widest hover:text-gray-900 transition-colors"
                  onClick={() => setShowRejectModal(false)}
                >
                  Return to Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReporterCompletedAds;
